import { useEffect } from 'react';
import {
  cancelAnimation,
  Easing,
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';

/** Replay the celebration in place. Progress zero and one both leave content at rest. */
export function useFooterCelebration(animate: boolean) {
  const progress = useSharedValue(0);
  useEffect(() => {
    cancelAnimation(progress);
    progress.set(0);
    if (animate) progress.set(withTiming(1, { duration: 1400, easing: Easing.linear }));
    return () => cancelAnimation(progress);
  }, [animate, progress]);

  const checkStyle = useAnimatedStyle(() => ({
    transform: [
      { scale: interpolate(progress.get(), [0, 0.07, 0.25, 0.43, 1], [1, 1, 1.22, 1, 1], Extrapolation.CLAMP) },
      { rotate: `${interpolate(progress.get(), [0, 0.07, 0.25, 0.43, 1], [0, 0, 8, 0, 0], Extrapolation.CLAMP)}deg` },
    ],
  }));
  const ringStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.get(), [0, 0.085, 0.2, 0.62, 1], [0, 0, 0.6, 0, 0], Extrapolation.CLAMP),
    transform: [{ scale: interpolate(progress.get(), [0, 0.085, 0.62, 1], [0.7, 0.7, 1.9, 1.9], Extrapolation.CLAMP) }],
  }));
  const artworkStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: interpolate(progress.get(), [0, 0.12, 0.4, 0.68, 1], [0, 0, -6, 0, 0], Extrapolation.CLAMP) },
      { scale: interpolate(progress.get(), [0, 0.12, 0.4, 0.68, 1], [1, 1, 1.035, 1, 1], Extrapolation.CLAMP) },
      { rotate: `${interpolate(progress.get(), [0, 0.12, 0.4, 0.68, 1], [0, 0, -1.5, 0, 0], Extrapolation.CLAMP)}deg` },
    ],
  }));
  const titleStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: interpolate(progress.get(), [0, 0.23, 0.4, 0.6, 1], [0, 0, -3, 0, 0], Extrapolation.CLAMP) },
    ],
  }));
  const descriptionStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: interpolate(progress.get(), [0, 0.31, 0.48, 0.68, 1], [0, 0, -3, 0, 0], Extrapolation.CLAMP) },
    ],
  }));
  const actionStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: interpolate(progress.get(), [0, 0.4, 0.6, 0.8, 1], [0, 0, -3, 0, 0], Extrapolation.CLAMP) },
      { scale: interpolate(progress.get(), [0, 0.4, 0.6, 0.8, 1], [1, 1, 1.025, 1, 1], Extrapolation.CLAMP) },
    ],
  }));
  return { progress, checkStyle, ringStyle, artworkStyle, titleStyle, descriptionStyle, actionStyle };
}

export function useConfettiParticleStyle(progress: SharedValue<number>, x: number, y: number, index: number) {
  return useAnimatedStyle(() => {
    const start = 0.1 + (index % 4) * 0.025;
    const phase = interpolate(progress.get(), [start, start + 0.78], [0, 1], Extrapolation.CLAMP);
    return {
      opacity: interpolate(phase, [0, 0.2, 0.65, 1], [0, 1, 1, 0], Extrapolation.CLAMP),
      transform: [
        { translateX: interpolate(phase, [0, 0.2, 0.65, 1], [0, x * 0.6, x, x * 1.08], Extrapolation.CLAMP) },
        { translateY: interpolate(phase, [0, 0.2, 0.65, 1], [0, y * 0.5 - 30, y, y + 45], Extrapolation.CLAMP) },
        { rotate: `${interpolate(phase, [0, 1], [0, 240], Extrapolation.CLAMP)}deg` },
        { scale: interpolate(phase, [0, 0.2, 0.65, 1], [0.4, 1, 1, 0.7], Extrapolation.CLAMP) },
      ],
    };
  });
}
